import leadsAPI from '../../../api/nexceo/leads';

const state = {
  records: [],
  activities: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  allLeads: $state => $state.records,

  leadsByStage: $state => stageId =>
    $state.records.filter(l => l.kanban_stage_id === stageId),

  leadsByProject: $state => projectId =>
    $state.records.filter(l => l.project_id === projectId),

  getLeadById: $state => id => $state.records.find(l => l.id === id),

  getActivitiesForLead: $state => leadId => $state.activities[leadId] || [],

  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  // M26.14 Part 5/13 — Live UI Synchronization. Called when the
  // nexceo_lead.updated websocket event fires (see helper/actionCable.js).
  // Refetches via the existing show endpoint and patches the store through
  // the existing UPDATE_LEAD mutation (a no-op if this lead isn't currently
  // loaded in this view) — no new serialization/payload contract, reuses
  // exactly what fetchLeads/updateLead already use.
  refreshLead: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.show(leadId);
    commit('UPDATE_LEAD', data);
  },

  fetchLeads: async ({ commit }, params = {}) => {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await leadsAPI.get(params);
      commit('SET_LEADS', data);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  createLead: async ({ commit }, leadData) => {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await leadsAPI.create({ lead: leadData });
      commit('ADD_LEAD', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  updateLead: async ({ commit }, { id, ...leadData }) => {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await leadsAPI.update(id, { lead: leadData });
      commit('UPDATE_LEAD', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  deleteLead: async ({ commit }, leadId) => {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await leadsAPI.delete(leadId);
      commit('REMOVE_LEAD', leadId);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  moveStage: async ({ commit }, { leadId, stageKey }) => {
    const { data } = await leadsAPI.moveStage(leadId, stageKey);
    commit('UPDATE_LEAD', data);
    return data;
  },

  assignAgent: async ({ commit }, { leadId, agentId }) => {
    const { data } = await leadsAPI.assignAgent(leadId, agentId);
    commit('UPDATE_LEAD', data);
    return data;
  },

  markLost: async ({ commit }, { leadId, reason }) => {
    const { data } = await leadsAPI.markLost(leadId, reason);
    commit('UPDATE_LEAD', data);
    return data;
  },

  markBooked: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.markBooked(leadId);
    commit('UPDATE_LEAD', data);
    return data;
  },

  pauseAi: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.pauseAi(leadId);
    commit('UPDATE_LEAD', data);
    return data;
  },

  resumeAi: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.resumeAi(leadId);
    commit('UPDATE_LEAD', data);
    return data;
  },

  pauseFollowUps: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.pauseFollowUps(leadId);
    commit('UPDATE_LEAD', data);
    return data;
  },

  resumeFollowUps: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.resumeFollowUps(leadId);
    commit('UPDATE_LEAD', data);
    return data;
  },

  fetchActivities: async ({ commit }, leadId) => {
    const { data } = await leadsAPI.getActivities(leadId);
    commit('SET_ACTIVITIES', { leadId, activities: data });
    return data;
  },
};

const mutations = {
  SET_LEADS: ($state, leads) => {
    $state.records = leads;
  },

  ADD_LEAD: ($state, lead) => {
    $state.records.unshift(lead);
  },

  UPDATE_LEAD: ($state, lead) => {
    const idx = $state.records.findIndex(l => l.id === lead.id);
    if (idx !== -1) {
      $state.records.splice(idx, 1, lead);
    }
  },

  REMOVE_LEAD: ($state, leadId) => {
    $state.records = $state.records.filter(l => l.id !== leadId);
  },

  SET_ACTIVITIES: ($state, { leadId, activities }) => {
    $state.activities = { ...$state.activities, [leadId]: activities };
  },

  SET_UI_FLAG: ($state, flags) => {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
